import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";

/**
 * Appointment history for a client row (expandable detail).
 */
export const ClientAppointmentsDetail = ({ data }) => {
  const appointments = data?.appointments || [];

  if (!appointments.length) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Appointment details are not available.
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Client: {data?.clientName} | Total Appointments:{" "}
          {data?.totalAppointments ?? 0}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ mb: 1.25, color: "text.primary" }}
      >
        Appointment History ({appointments.length} appointment
        {appointments.length !== 1 ? "s" : ""})
      </Typography>
      <TableContainer
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "6px",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              bgcolor: "secondary.main",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              "& .MuiTableCell-head": {
                color: "common.white",
                fontWeight: 600,
                fontSize: "0.75rem",
                py: 1,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Stylist</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments.map((appt, index) => (
              <TableRow
                key={`${appt.appointmentDate || "appt"}-${index}`}
                sx={{
                  "&:nth-of-type(odd)": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "#1b1b1e" : "#fafafa",
                  },
                  "&:nth-of-type(even)": {
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark" ? "#141416" : "transparent",
                  },
                  "& td": {
                    py: 1,
                    verticalAlign: "top",
                    color: "text.primary",
                    borderColor: "divider",
                  },
                }}
              >
                <TableCell>
                  {appt.appointmentDate
                    ? new Date(appt.appointmentDate).toLocaleDateString()
                    : "—"}
                </TableCell>
                <TableCell>{appt.stylistName || "N/A"}</TableCell>
                <TableCell>
                  {appt.services && appt.services.length > 0 ? (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {appt.services.map((service, idx) => (
                        <Chip
                          key={`${service}-${idx}`}
                          label={service}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{
                            height: 22,
                            fontSize: "0.7rem",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No services listed
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={appt.status || "—"}
                    size="small"
                    variant="outlined"
                    color={
                      appt.status === "Completed"
                        ? "success"
                        : appt.status === "Canceled"
                          ? "error"
                          : appt.status === "No-Show"
                            ? "warning"
                            : "default"
                    }
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  {appt.amount != null
                    ? `$${Number(appt.amount).toFixed(2)}`
                    : "$0.00"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
